import { getSelectedSensorIds, getValue } from '@Selectors';
import { SensorGroupFormValues } from '@Types/sensorGroup.types';
import { Formik, FormikProps, FormikTouched, Form } from 'formik';
import { default as React, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { sensorGroupSchema } from '../../schemas/sensorGroup.schema';
import usePropagateFormikDependant from './hooks/usePropagateFormikDependant';
import UpsertSensorGroupFields from './UpsertSensorGroupFields';
import { getSensorsList } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations/useGlobalization';

interface UpsertSensorGroupFormProps {
	formRef: React.MutableRefObject<
		FormikProps<SensorGroupFormValues> | undefined
	>;
	setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
	onSubmit: (values: SensorGroupFormValues) => void;
	setSensorGroupName?: React.Dispatch<React.SetStateAction<string>>;
}

const UpsertSensorGroupForm = ({
	formRef,
	setIsValid,
	onSubmit,
	setSensorGroupName,
}: UpsertSensorGroupFormProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const selectedSensors = useSelector(getSelectedSensorIds);
	const sensors = selectedSensors.map(sensor => sensor.sensorId);

	const { sensorGroupId } = useParams<{ sensorGroupId: string }>();
	const { sensorGroup } = useSelector(getValue).sensorGroup.sensorGroupById;

	const isUpdate = !!sensorGroupId;

	const initialTouched: FormikTouched<SensorGroupFormValues> =
		React.useMemo(() => {
			if (isUpdate) return { sensors: true };
			return {};
		}, [isUpdate]);

	const initialValues: SensorGroupFormValues = React.useMemo(() => {
		if (!isUpdate) return { name: '', sensors: [] };
		return { ...sensorGroup, sensors };
	}, [isUpdate, sensorGroup, sensors]);

	return (
		<Formik
			initialValues={initialValues}
			initialTouched={initialTouched}
			validationSchema={sensorGroupSchema(t)}
			enableReinitialize
			validateOnMount
			onSubmit={onSubmit}>
			{formikProps => {
				formRef.current = formikProps;
				// eslint-disable-next-line
				usePropagateFormikDependant({
					setIsValid,
					setSensorGroupName,
					formikProps,
				});
				// eslint-disable-next-line
				useEffect(() => {
					dispatch(getSensorsList({}));
				}, []);
				return (
					<Form>
						<UpsertSensorGroupFields />
					</Form>
				);
			}}
		</Formik>
	);
};

export default UpsertSensorGroupForm;
